/** @module displayComponents/DisplayElements */

import {
	ConstrainDimensionsSelector,
	IsPreviewGenerator,
} from "../../state/ui";
import {
	PageSizeAtom,
	StickyFooterHeightAtom,
	StickyHeaderHeightAtom,
} from "../../state/page";
import { elementAlign, startingPoint } from "../../lib/cssHelpers";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import type { ElementsProps } from "../../@types/viewerati";
import Sticky from "./Sticky";
// @ts-expect-error: No types for this package
import smartquotes from "smartquotes";
import styled from "styled-components";

const StyledStickyElements = styled.div<
	Omit<ElementsProps, "$stickyHeaderHeight"> & { $position: "top" | "bottom" }
>`
  ${(props) => elementAlign(props.theme.align, false)};
  position: ${(props) => (props.$constrainDimensions ? "absolute" : "fixed")};
  ${(props) => props.$position}: 0;
  display: flex;
  flex-direction: column;
  height: fit-content; // safari needs this
  justify-content: ${(props) => startingPoint(props.theme.startingPoint, false, props.$isInLandscape)};
  width: 100%;
  z-index: 2;
  pointer-events: none;
`;

/**
 * Gets the state holding the elements that are to be displayed in the current page
 * and returns a JSX element containing them with CSS and theming applied.
 * @returns JSX
 */
function DisplaySticky({ position }: { position: "top" | "bottom" }) {
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const pageSize = useRecoilValue(PageSizeAtom);
	const setStickyHeaderHeight = useSetRecoilState(StickyHeaderHeightAtom);
	const setStickyFooterHeight = useSetRecoilState(StickyFooterHeightAtom);
	const stickyRef = useRef<HTMLDivElement>(null);

	const baseFontSize = pageSize.baseFontSize;

	// Use useLayoutEffect to measure and set height before the browser paints
	useLayoutEffect(() => {
		const stickyElement = stickyRef.current;
		if (!stickyElement) return;

		// Use ResizeObserver for performance
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (position === "top") {
					setStickyHeaderHeight(entry.target.clientHeight);
				} else {
					setStickyFooterHeight(entry.target.clientHeight);
				}
			}
		});

		observer.observe(stickyElement);

		// Cleanup observer on unmount
		return () => observer.disconnect();
	}, [setStickyHeaderHeight, setStickyFooterHeight, position]);

	// add smartquotes to the sticky content when it renders
	useEffect(() => {
		if (stickyRef.current) {
			smartquotes(stickyRef.current);
		}
	}, [position, pageSize]);
	return (
		// biome-ignore lint/a11y/useValidAriaRole: <explanation>
		<StyledStickyElements
			ref={stickyRef}
			role="none"
			$baseFontSize={baseFontSize}
			data-testid={`DisplaySticky-${position}`}
			className="display-sticky-menu"
			$isPreviewer={isPreviewer}
			$constrainDimensions={constrainDimensions}
			$isInLandscape={pageSize.landscape}
			$position={position}
		>
			<Sticky position={position} />
		</StyledStickyElements>
	);
}

export default DisplaySticky;
